import { useState, useEffect } from "react";
import {
  Drawer,
  TextField,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { Dropdown } from "@fluentui/react";
// Added by Gauri to fix dropdown position issue on 20 Nov 2025
import { DirectionalHint } from "@fluentui/react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import useUpdateROI from "../../../hooks/Editpage/useUpdateROI";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { toast } from "react-toastify";
import AxiosInstance1 from "app/hooks/AxiosInstance/AxiosInstance1";

// Mapping month names to numbers
const monthToNumber = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};

const ROIDetailsDrawer = ({
  open,
  onClose,
  initialData,
  onSave,
  initiativesID,
  initiativeDetail,
  setRefresh1,
  refresh,
  acc,
  initiativeROI,
}) => {
  const [month, setMonth] = useState(
    initialData ? initialData.month : "January"
  );
  const [year, setYear] = useState(initialData ? initialData.year : "2023");
  // Added By Madhuri.K On 31-10-2025 - Helper function to remove commas from value
  const removeCommas = (value) => {
    if (!value) return "";
    return String(value).replace(/,/g, "");
  };

  // Added By Madhuri.K On 31-10-2025 - Helper function to format number with commas for display
  const formatWithCommas = (value) => {
    if (!value || value === "") return "";
    // Remove any existing commas first
    const cleaned = removeCommas(value);
    if (cleaned === "") return "";
    
    // Added By Madhuri.K On 31-10-2025 - Handle values that start with decimal point (e.g., ".5")
    const startsWithDecimal = cleaned.startsWith('.');
    
    // Split by decimal point if exists
    const parts = cleaned.split('.');
    const integerPart = parts[0] || ''; // Added By Madhuri.K On 31-10-2025 - Handle empty integer part
    const decimalPart = parts[1] || ''; // Added By Madhuri.K On 31-10-2025 - Handle empty decimal part
    
    // Add commas to integer part (only if it has digits)
    let formattedInteger = '';
    if (integerPart) {
      formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    
    // Added By Madhuri.K On 31-10-2025 - Combine with decimal part properly (handles ".5", "5.", "5.5" formats)
    if (startsWithDecimal) {
      return `.${decimalPart}`; // Return as ".5" format
    } else if (decimalPart) {
      return formattedInteger ? `${formattedInteger}.${decimalPart}` : `.${decimalPart}`;
    } else if (cleaned.endsWith('.')) {
      return `${formattedInteger}.`; // Return as "5." format (trailing decimal)
    } else {
      return formattedInteger || integerPart; // Return integer only
    }
  };

  const [projectedROI, setProjectedROI] = useState(
    initialData ? removeCommas(initialData.projectedROI) : "" // Added By Madhuri.K On 31-10-2025 - Remove commas from initial value
  );
  // Below Line Added By Durgesh Dalvi : To ensure it is the value which do not changes when the form value changes
  const [actualProjectedROI, setActualProjectedROI] = useState(
    initialData ? removeCommas(initialData.projectedROI) : "" // Added By Madhuri.K On 31-10-2025 - Remove commas from initial value
  );
  const [monthlyData, setMonthlyData] = useState([]);
  const [editedData, setEditedData] = useState([]);
  //Added By Durgesh.D
  const [editableFields, setEditableFields] = useState([]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [errors, setErrors] = useState({
    month: "",
    year: "",
    projectedROI: "",
  });
  // Added by Gauri to get Initiative End Date on 07 Mar 2025
  const [initiativeEndDate, setInitiativeEndDate] = useState(
    initiativeDetail.data.listInitiativeDetailEntity[11].controlValue
  );
  const [formState, setFormState] = useState({
    costCategory: "",
    amount: "",
    costType: "", // This will hold 0 or 1 for Recurring or Fixed
    fromDate: null,
    toDate: null,
    description: "",
  });
  const yearRange = Array.from({ length: 50 }, (_, i) => 1990 + i);
  const userdata = JSON.parse(sessionStorage.getItem("user"));
  const employeeId = userdata?.employeeId;
  const { updateROI, loading, error } = useUpdateROI(initiativesID); // Hook to update ROI

  console.log("employeeId", initialData);
  console.log("ROI initiativeEndDate:", initiativeEndDate);

  // Added by Gauri to add validation alert for Initiative End Date on 07 Mar 2025
  const shortMonthToNumber = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
  };

  let initiativeEndYear = null;
  let initiativeEndMonthNumber = null;
  let initiativeEndMonthName = null;

  // Extract user-selected values
  const newYear = parseInt(year, 10); // User-selected year
  const newMonthNumber = monthToNumber[month]; // User-selected month

  if (initiativeEndDate) {
    // Extract parts of the date string
    const parts = initiativeEndDate.split(" "); // Splitting by space: ["Feb", "28", "2025", "12:00AM"]

    if (parts.length >= 3) {
      const monthName = parts[0]; // "Feb"
      const year = parseInt(parts[2], 10); // "2025"

      initiativeEndYear = isNaN(year) ? null : year; // Ensure it's a valid number
      initiativeEndMonthNumber = shortMonthToNumber[monthName]; // Convert "Feb" -> 2
      initiativeEndMonthName = monthName; // Already in correct format
    }
  }

  const handleSaveROI = async () => {
    // Modified mandatory alerts by Gauri on 31 Mar 2025
    if (!month) {
      toast.error("Month should not be left blank.");
      return; // Stop execution if fields are missing
    }
    if (!year) {
      toast.error("Year should not be left blank.");
      return; // Stop execution if fields are missing
    }
    if (!month || !year || !projectedROI) {
      toast.error("Projected ROI should not be left blank.");
      return; // Stop execution if fields are missing
    }

    // Convert values to numbers for comparison
    // const newProjectedROI = parseFloat(projectedROI);
    // Changed newProjectedROI format by Gauri for giving alert on 25 Mar 2025
    const newProjectedROI = projectedROI
      ? parseFloat(projectedROI.toString().replace(/,/g, "")) || 0
      : 0;
    const existingProjectedROI = parseFloat(actualProjectedROI);

    const totalMonthlySum = editedData.reduce((sum, item) => {
      return (
        sum +
        (parseFloat(item.jan) || 0) +
        (parseFloat(item.feb) || 0) +
        (parseFloat(item.mar) || 0) +
        (parseFloat(item.april) || 0) +
        (parseFloat(item.may) || 0) +
        (parseFloat(item.june) || 0) +
        (parseFloat(item.july) || 0) +
        (parseFloat(item.aug) || 0) +
        (parseFloat(item.sep) || 0) +
        (parseFloat(item.oct) || 0) +
        (parseFloat(item.nov) || 0) +
        (parseFloat(item.dec) || 0)
      );
    }, 0);

    // Check if the new Projected ROI is lower than the existing one
    // if (newProjectedROI < existingProjectedROI) {
    if (newProjectedROI < totalMonthlySum) {
      toast.error(`Value entered for Projected ROI is less than total sum of all months i.e. ${totalMonthlySum}.
        \n \n You will require to modify Amount distribution for each month first.`);
      return;
    }

    // Ensure initiativeROI is available
    try {
      // Prepare the request payload for both POST and PUT
      let requestData;

      if (selectedTab === 0) {
        if (initiativeROI?.length > 0) {
          // Sort to find the latest record
          const sortedList = [...initiativeROI].sort((a, b) => {
            if (a.roiYear !== b.roiYear) {
              return a.roiYear - b.roiYear;
            }
            return monthToNumber[a.monthName] - monthToNumber[b.monthName];
          });
          const lastRecord = sortedList[sortedList.length - 1]; // Get the latest record
          const newMonthNumber = monthToNumber[month];
          const newYear = parseInt(year, 10);
          const lastMonthNumber = monthToNumber[lastRecord.monthName];
          const lastYear = lastRecord.roiYear;

          // Modified by Gauri to check validation for existing record on 12 Mar 2025
          // Skip validation if editing the same record
          if (initialData?.id) {
            const isEditingSameRecord =
              initialData.month === month &&
              initialData.year.toString() === year;

            if (!isEditingSameRecord) {
              // Validate if it's a new entry or different edit
              if (
                newYear < lastYear ||
                (newYear === lastYear && newMonthNumber <= lastMonthNumber)
              ) {
                toast.error(
                  `Enter Month and Year Date combination greater than '${lastRecord.monthName}-${lastRecord.roiYear}'`
                );
                return;
              }
            }
          } else {
            // Validation for new records only
            if (
              newYear < lastYear ||
              (newYear === lastYear && newMonthNumber <= lastMonthNumber)
            ) {
              toast.error(
                `Enter Month and Year Date combination greater than '${lastRecord.monthName}-${lastRecord.roiYear}'`
              );
              return;
            }
          }
        }

        // Added by Gauri to add validation alert "Enter Month and Year Date combination greater than Initiative End Date on 07 Mar 2025
        if (initiativeEndYear && initiativeEndMonthNumber) {
          if (
            newYear < initiativeEndYear ||
            (newYear === initiativeEndYear &&
              newMonthNumber <= initiativeEndMonthNumber)
          ) {
            toast.error(
              `Enter Month and Year Date combination greater than '${initiativeEndMonthName}-${initiativeEndYear}'`
            );
            return;
          }
        }
        // End of Gauri to add validation alert "Enter Month and Year Date combination greater than Initiative End Date on 07 Mar 2025

        if (initialData?.id) {
          // If no initialData, use POST to create new ROI details
          const postUrl = `/api/InitiativeDetail/UpdateInitiativeROI?ROIID=${initialData.id}&ROIMonth=${monthToNumber[month]}&ROIYear=${year}&ProjectedROI=${projectedROI}&UserID=${employeeId}&IdeaId=${initiativesID}`;
          const postResponse = await AxiosInstance1.put(postUrl, null, {
            // headers: {
            //   Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            // },
          });

          if (postResponse.data) {
            toast.success("ROI Details Updated Successfully.");
            setRefresh1(!refresh);
            onClose();
            fetchCostMonthlyDistribution();
          } else {
            toast.error("Failed to save ROI details.");
          }
        } else {
          const putUrl = `/api/InitiativeDetail/PostInitiativeROI?ROIMonth=${monthToNumber[month]}&ROIYear=${year}&ProjectedROI=${projectedROI}&UserID=${employeeId}&IdeaId=${initiativesID}`;
          const putResponse = await AxiosInstance1.post(putUrl, null, {
            // headers: {
            //   Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
            // },
          });
          // Commented By Madhuri.K on 17-04-2025
          // if (putResponse.data) {
          //   toast.success("ROI Details Save successfully.");
          //   setRefresh1(!refresh);
          //   onClose();
          //   fetchCostMonthlyDistribution();
          // } else {
          //   toast.error("Failed to update ROI details.");
          // }
          // Code Added By Madhuri.K on 18-04-2025
          const errorMsg = putResponse.data?.data?.[0]?.result || "";
          const isValidationError =
            putResponse.data?.success === false ||
            errorMsg.toLowerCase().includes("enter month and year");

          if (isValidationError) {
            toast.error(errorMsg || "Validation failed from server.");
          } else if (putResponse.data) {
            toast.success("ROI Details Save successfully.");
            setRefresh1(!refresh);
            onClose();
            fetchCostMonthlyDistribution();
          } else {
            toast.error("Failed to update ROI details.");
          }
        }
      }
    } catch (error) {
      console.error("Error saving ROI details or monthly distribution:", error);
      toast.error("An error occurred while saving.");
    }
  };

  // Modified By Durgesh Dalvi: to pass pn properly formatted data to the api
  const handleSaveMonthDistribution = async () => {
    try {
      // Added by Gauri to add alert for blank values on 05 Mar 2025
      const hasBlankValue = editedData.some((item, index) => {
        return [
          "jan",
          "feb",
          "mar",
          "april",
          "may",
          "june",
          "july",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
        ].some((month) => {
          const value = item[month];
          const initialValue = editableFields[index]?.[month]; // Get the initial value

          // Only show an error if the user cleared a previously filled value
          return (
            initialValue !== "" &&
            initialValue !== null &&
            initialValue !== undefined &&
            value === ""
          );
        });
      });

      if (hasBlankValue) {
        toast.error("Monthly Amount should not be blank.");
        return;
      }
      // End of Added by Gauri to add alert for blank values on 05 Mar 2025

      const hasNegativeValue = editedData.some((item) => {
        return [
          "jan",
          "feb",
          "mar",
          "april",
          "may",
          "june",
          "july",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
        ].some((month) => {
          const value = item[month];
          return value < 0;
        });
      });

      if (hasNegativeValue) {
        toast.error("Monthly Amount should not be a negative value.");
        return;
      }

      const hasInvalidCharacter = editedData.some((item) =>
        [
          "jan",
          "feb",
          "mar",
          "april",
          "may",
          "june",
          "july",
          "aug",
          "sep",
          "oct",
          "nov",
          "dec",
        ].some((month) => {
          const value = item[month];
          return (
            value !== null &&
            value !== undefined &&
            value !== "" &&
            !/^\d*\.?\d+$/.test(value)
          );
        })
      );

      if (hasInvalidCharacter) {
        toast.error("Monthly Amount should contain only positive value");
        return;
      }

      // Added by Gauri to add validation for sum of all month values on 19 Mar 2025
      const totalMonthlySum = editedData.reduce((sum, item) => {
        return (
          sum +
          (parseFloat(item.jan) || 0) +
          (parseFloat(item.feb) || 0) +
          (parseFloat(item.mar) || 0) +
          (parseFloat(item.april) || 0) +
          (parseFloat(item.may) || 0) +
          (parseFloat(item.june) || 0) +
          (parseFloat(item.july) || 0) +
          (parseFloat(item.aug) || 0) +
          (parseFloat(item.sep) || 0) +
          (parseFloat(item.oct) || 0) +
          (parseFloat(item.nov) || 0) +
          (parseFloat(item.dec) || 0)
        );
      }, 0);

      // Changed actualROIValue format by Gauri to remove unwanted spaces on 25 Mar 2025
      const actualROIValue = Number(actualProjectedROI.replace(/,/g, ""));

      // if (exceedsAmount) {
      if (totalMonthlySum > Number(actualROIValue)) {
        toast.error(
          // `Total of all monthly distribution should not exceed Amount ${actualProjectedROI}`
          `Total of all monthly amounts should not exceed 'Projected ROI' amount ${actualProjectedROI}`
        );
        return;
      }
      // End of Added by Gauri to add validation for sum of all month values on 19 Mar 2025

      const requestData = {
        roiid: initialData.id,
        monthlyDistributionROI: editedData.map((item) => ({
          roiDivisionID: item.roiid,
          jan:
            item.jan !== null && item.jan !== undefined
              ? parseFloat(item.jan)
              : null,
          feb:
            item.feb !== null && item.feb !== undefined
              ? parseFloat(item.feb)
              : null,
          mar:
            item.mar !== null && item.mar !== undefined
              ? parseFloat(item.mar)
              : null,
          apr:
            item.april !== null && item.april !== undefined
              ? parseFloat(item.april)
              : null,
          may:
            item.may !== null && item.may !== undefined
              ? parseFloat(item.may)
              : null,
          jun:
            item.june !== null && item.june !== undefined
              ? parseFloat(item.june)
              : null,
          jul:
            item.july !== null && item.july !== undefined
              ? parseFloat(item.july)
              : null,
          aug:
            item.aug !== null && item.aug !== undefined
              ? parseFloat(item.aug)
              : null,
          sep:
            item.sep !== null && item.sep !== undefined
              ? parseFloat(item.sep)
              : null,
          oct:
            item.oct !== null && item.oct !== undefined
              ? parseFloat(item.oct)
              : null,
          nov:
            item.nov !== null && item.nov !== undefined
              ? parseFloat(item.nov)
              : null,
          dec:
            item.dec !== null && item.dec !== undefined
              ? parseFloat(item.dec)
              : null,
        })),
      };

      console.log("requestData:", JSON.stringify(requestData));

      const response = await AxiosInstance1.post(
        `/api/InitiativeDetail/PostROIMonthlyDistribution`,
        requestData,
        {
          // method: "POST",
          headers: {
            accept: "*/*",
            "Content-Type": "application/json",
            // Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
          },
          // body: JSON.stringify(requestData),
        }
      );

      const result = response?.data;

      if (result) {
        toast.success("Monthly distribution saved successfully.");
      } else {
        toast.error("Failed to save the data.");
      }
    } catch (error) {
      console.error("Error saving monthly distribution:", error);
      toast.error("An error occurred while saving.");
    }
  };

  // Added by Gauri to fix refreshment issue when tab change on 01 Apr 2025
  useEffect(() => {
    if (selectedTab === 0) {
      fetchCostMonthlyDistribution();
    } else if (selectedTab === 1) {
      fetchCostMonthlyDistribution();
    }
  }, [selectedTab]);

  const saveMonthlyDistribution = async (editedData) => {
    // Extract the monthly data
    const monthlyData = editedData[0]; // Assuming editedData has a single object

    // Prepare the request body, sending only editedData[0]
    const requestBody = { ...monthlyData };

    // Make the API request
    const response = await AxiosInstance1.post(
      `/api/InitiativeDetail/PostROIMonthlyDistribution`,
      requestBody,
      {
        // method: "POST",
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
          // Authorization: `Bearer ${sessionStorage.getItem("access_token")}`,
        },
        // body: JSON.stringify(requestBody),
      }
    );

    const result = response?.data;
    return result;
  };

  /* const handleFieldChange = (year, month, value) => {
    // Ensure the value is not less than 0
    const validatedValue = Math.max(1, value);

    setEditedData((prevData) =>
      prevData.map((item) => (item.year === year ? { ...item, [month]: value } : item))
    );
  }; */

  // Modified by Durgesh.D
  const handleFieldChange = (year, month, value) => {
    // Added by Gauri to Prevent negative numbers on 05 Mar 2025
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    setEditedData((prevData) =>
      prevData.map((item) =>
        item.year === year
          ? { ...item, [month]: value } // Store null as null, don't convert to -1 here
          : item
      )
    );
  };

  const fetchCostMonthlyDistribution = async () => {
    const ROIID = initialData?.id;
    const token = sessionStorage.getItem("access_token");
    console.log("fetchCostMonthlyDistribution1", formState);
    if (!token) {
      console.error("Access token is missing in session storage.");
      return null;
    }

    const apiUrl = `/api/InitiativeDetail/GetInitiativeROIDetails?ROIID=${ROIID}`;

    try {
      const response = await AxiosInstance1.get(apiUrl, {
        // headers: {
        //   Authorization: `Bearer ${token}`,
        // },
      });

      if (response.data.data && response.data.data.listROIMonthlyDistribution) {
        setMonthlyData(response.data.data.listROIMonthlyDistribution);
        //Durgesh
        const transformedData =
          response.data.data.listROIMonthlyDistribution.map((item) => ({
            ...item,
            jan: item.jan === null || item.jan === undefined ? "" : item.jan,
            feb: item.feb === null || item.feb === undefined ? "" : item.feb,
            mar: item.mar === null || item.mar === undefined ? "" : item.mar,
            april:
              item.april === null || item.april === undefined ? "" : item.april,
            may: item.may === null || item.may === undefined ? "" : item.may,
            june:
              item.june === null || item.june === undefined ? "" : item.june,
            july:
              item.july === null || item.july === undefined ? "" : item.july,
            aug: item.aug === null || item.aug === undefined ? "" : item.aug,
            sep: item.sep === null || item.sep === undefined ? "" : item.sep,
            oct: item.oct === null || item.oct === undefined ? "" : item.oct,
            nov: item.nov === null || item.nov === undefined ? "" : item.nov,
            dec: item.dec === null || item.dec === undefined ? "" : item.dec,
          }));

        setEditedData(transformedData);
        setEditableFields(transformedData);
      }
    } catch (error) {
      console.error("Error fetching cost monthly distribution data:", error);
      return null;
    }
  };
  // Added By Madhuri.K On 31-10-2025 - Reset form fields when drawer opens or initialData changes
  useEffect(() => {
    if (open) {
      // Reset form fields when drawer opens or when editing different record
      setMonth(initialData ? initialData.month : "January");
      // Added by Gauri to ensure year is always stored as string for Fluent UI Dropdown compatibility on 20 Nov 2025
      setYear(initialData ? String(initialData.year) : "2023");
      // Added By Madhuri.K On 31-10-2025 - Remove commas from Projected ROI when resetting
      const roiValue = initialData ? removeCommas(initialData.projectedROI) : "";
      setProjectedROI(roiValue);
      setActualProjectedROI(roiValue);
      fetchCostMonthlyDistribution();
    }
  }, [open, initialData?.id]); // Added By Madhuri.K On 31-10-2025 - Added initialData?.id as dependency to reset when editing different record

  // Added by Gauri on 20 Nov 2025 - Common calloutProps for all dropdowns
  const commonCalloutProps = {
    // Use bottomAutoEdge which tries to open downward first, then automatically adjusts upward if needed
    directionalHint: DirectionalHint.bottomAutoEdge,
    // Set max height to prevent dropdown from being too large
    calloutMaxHeight: 250,
    // Ensure dropdown stays within viewport bounds
    bounds: typeof window !== 'undefined' ? {
      top: 0,
      left: 0,
      bottom: window.innerHeight,
      right: window.innerWidth
    } : undefined
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100%", sm: "80vw" }, // Set width to 100% for extra-small screens, 600px for small screens and above
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 2,
            backgroundColor: "#f5f5f5", // Set the background color here (light gray example)
            padding: 2, // Add padding if needed to create spacing around the content
          }}
        >
          <h5>ROI Details</h5>
          <IconButton onClick={onClose}>
            <Tooltip title="Close">
              <CloseIcon />
            </Tooltip>
          </IconButton>
        </Box>

        <Box sx={{ marginBottom: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Details" sx={{ textTransform: "none" }} />
            {initialData?.projectedROI && (
              <Tab
                label="Monthly Distribution"
                sx={{ textTransform: "none" }}
              />
            )}
          </Tabs>
        </Box>

        {selectedTab === 0 && ( // Show input fields only in the "Details" tab
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {acc[2]?.access !== 0 && (
              <Box
                sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                <PrimaryButton onClick={handleSaveROI} disabled={loading}>
                  {loading ? "Saving..." : "Save"}
                </PrimaryButton>
              </Box>
            )}
            {/* Code Added By Madhuri.K On 27-Dec-2024 for Mandatory sign Comment start here */}
            <div className="col-sm-12 text-end form-group">
              <label className="form-label IM_label">
                (<span style={{ color: "red" }}>*</span> Mandatory)
              </label>
            </div>
            {/* Code Added By Madhuri.K On 27-Dec-2024 for Mandatory sign Comment end here */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                {/* Modified by Gauri to convert to Fluent UI Dropdown and fix dropdown position issue on 20 Nov 2025 */}
                <Dropdown
                  label={
                    <>
                      Month <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  options={[
                    { key: "", text: "Select Month" },
                    ...Object.keys(monthToNumber).map((m) => ({
                      key: m,
                      text: m
                    }))
                  ]}
                  selectedKey={month || ""}
                  onChange={(e, option) => setMonth(option?.key || "")}
                  styles={{ 
                    root: { width: "100%" },
                    dropdown: { width: "100%" },
                    title: { 
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%"
                    }
                  }}
                  calloutProps={commonCalloutProps}
                />
                {errors.month && (
                  <span style={{ color: "red" }}>{errors.month}</span>
                )}
              </Box>
              <Box sx={{ flex: 1 }}>
                {/* Modified by Gauri to convert to Fluent UI Dropdown and fix dropdown position issue on 20 Nov 2025 */}
                <Dropdown
                  label={
                    <>
                      Year <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  options={[
                    { key: "", text: "Select Year" },
                    ...yearRange.map((yearValue) => ({
                      key: String(yearValue),
                      text: String(yearValue)
                    }))
                  ]}
                  selectedKey={year ? String(year) : ""}
                  onChange={(e, option) => {
                    // Ensure year is stored as string to maintain compatibility with existing code
                    const selectedYear = option?.key || "";
                    setYear(selectedYear);
                  }}
                  styles={{ 
                    root: { width: "100%" },
                    dropdown: { width: "100%" },
                    title: { 
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%"
                    }
                  }}
                  calloutProps={commonCalloutProps}
                />
                {errors.year && (
                  <span style={{ color: "red", fontSize: "12px" }}>
                    {errors.year}
                  </span>
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                <label>
                  Projected ROI <span style={{ color: "red" }}>*</span>
                </label>
                <TextField
                  value={formatWithCommas(projectedROI)}    // Added By Madhuri.K On 31-10-2025 - Display value with commas for better readability
                  onChange={(e) => {
                    // Added by Gauri to validate Projected ROI format on 31 Oct 2025
                    // Added By Madhuri.K On 31-10-2025 - Improved handling for clearing the field, removing commas, and allowing decimals
                    let newValue = e.target.value;
                    // Added By Madhuri.K On 31-10-2025 - Remove commas first before validation
                    newValue = removeCommas(newValue);
                    // Allow empty string - user can clear the field
                    if (newValue === "" || newValue === null || newValue === undefined) {
                      setProjectedROI("");
                      return;
                    }
                    // Only allow digits and decimal point
                    if (!/^[\d.]*$/.test(newValue)) {
                      return;
                    }
                    // Check for multiple decimal points - only allow one decimal point
                    if ((newValue.match(/\./g) || []).length > 1) {
                      return;
                    }
                    // Split by decimal for validation
                    const parts = newValue.split('.');
                    const integerPart = parts[0] || ''; // Added By Madhuri.K On 31-10-2025 - Handle empty integer part (e.g., ".5")
                    const decimalPart = parts[1] || ''; // Added By Madhuri.K On 31-10-2025 - Handle empty decimal part (e.g., "5.")
                    
                    // Added By Madhuri.K On 31-10-2025 - Calculate total digits (integer + decimal parts combined)
                    const totalDigits = (integerPart + decimalPart).replace(/\D/g, '').length;
                    
                    // Added By Madhuri.K On 31-10-2025 - Validate total digits: max 9 digits including decimal (integer + decimal combined)
                    if (totalDigits > 9) {
                      return; // Block if total digits exceed 9
                    }
                    // Added By Madhuri.K On 31-10-2025 - Allow decimal values: can start with decimal (".5"), can end with decimal ("5."), or have both ("5.5")
                    // If all validations pass, update the state (store without commas)
                    setProjectedROI(newValue);
                  }}
                  // Added by Gauri to validate Projected ROI format on 31 Oct 2025
                  // Added By Madhuri.K On 31-10-2025 - Format with commas on blur for display
                  onBlur={(e) => {
                    // Validate format on blur only
                    let value = e.target.value;
                    // Remove commas first
                    value = removeCommas(value);
                    
                    if (value && value !== "") {
                      // Remove invalid characters, keep only digits and one decimal point
                      let cleaned = value.replace(/[^\d.]/g, '');
                      // Remove multiple decimal points, keep only first one
                      const decimalIndex = cleaned.indexOf('.');
                      if (decimalIndex !== -1) {
                        cleaned = cleaned.substring(0, decimalIndex + 1) + cleaned.substring(decimalIndex + 1).replace(/\./g, '');
                      }
                      // Added By Madhuri.K On 31-10-2025 - Limit total digits to 9 (integer + decimal combined)
                      const parts = cleaned.split('.');
                      const integerPart = parts[0] || '';
                      const decimalPart = parts[1] || '';
                      
                      // Added By Madhuri.K On 31-10-2025 - Calculate total digits
                      const totalDigits = (integerPart + decimalPart).replace(/\D/g, '').length;
                      
                      if (totalDigits > 9) {
                        // Added By Madhuri.K On 31-10-2025 - Limit to 9 total digits
                        const allDigits = (integerPart + decimalPart).replace(/\D/g, '');
                        const limitedDigits = allDigits.substring(0, 9);
                        
                        // Distribute digits: try to keep as many integer digits as possible
                        if (integerPart) {
                          const intLength = Math.min(integerPart.length, 9);
                          const intPart = limitedDigits.substring(0, intLength);
                          const decPart = limitedDigits.substring(intLength);
                          cleaned = decPart ? `${intPart}.${decPart}` : intPart;
                        } else {
                          // If starts with decimal point
                          cleaned = '.' + limitedDigits;
                        }
                      }
                      // Store cleaned value (without commas) in state
                      setProjectedROI(cleaned);
                    } else {
                      // If empty, clear the field
                      setProjectedROI("");
                    }
                  }}
                  type="text" // Use text to enable regex validation
                  inputProps={{
                    inputMode: "decimal", // Ensure appropriate keyboard for numeric input
                  }}
                  fullWidth
                  InputProps={{
                    style: { height: 36 }, // Adjust height for TextField
                  }}
                />

                {errors.projectedROI && (
                  <span style={{ color: "red" }}>{errors.projectedROI}</span>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {selectedTab === 1 && ( // Show blank space for "Monthly Distribution" tab
          <Box sx={{ padding: 2 }}>
            {acc[2]?.access !== 0 && (
              <div style={{ textAlign: "right" }} className="mb-2">
                <PrimaryButton onClick={handleSaveMonthDistribution}>
                  Save
                </PrimaryButton>
              </div>
            )}
            <table className="table table-bordered">
              <thead>
                <tr>
                  {/* Added col-sm-1 by Gauri to make consistent columns on 08 Feb 2025 */}
                  <th>Year</th>
                  <th className="col-sm-1">January</th>
                  <th className="col-sm-1">February</th>
                  <th className="col-sm-1">March</th>
                  <th className="col-sm-1">April</th>
                  <th className="col-sm-1">May</th>
                  <th className="col-sm-1">June</th>
                  <th className="col-sm-1">July</th>
                  <th className="col-sm-1">August</th>
                  <th className="col-sm-1">September</th>
                  <th className="col-sm-1">October</th>
                  <th className="col-sm-1">November</th>
                  <th className="col-sm-1">December</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((item) => (
                  <tr key={item.year}>
                    <td>{item.year}</td>
                    {[
                      "jan",
                      "feb",
                      "mar",
                      "april",
                      "may",
                      "june",
                      "july",
                      "aug",
                      "sep",
                      "oct",
                      "nov",
                      "dec",
                    ].map((month, index) => {
                      //const currentValue =
                      //editedData.find((i) => i.year === item.year)?.[month] || "";

                      // const isEditable = currentValue !== ""; // Make it editable only if the data is present

                      //Durgesh

                      const currentValue = editedData.find(
                        (i) => i.year === item.year
                      )?.[month];

                      // const displayValue =
                      //  currentValue === null || currentValue === undefined ? "" : currentValue;

                      const initialValue = editableFields.find(
                        (i) => i.year === item.year
                      )?.[month];
                      const isEditable =
                        initialValue !== "" && initialValue !== undefined;

                      return (
                        //Modified By Durgesh Dalvi : implemented disabled attribute
                        <td key={month}>
                          <Tooltip title={`Current value: ${currentValue}`}>
                            <input
                              className="form-control"
                              value={currentValue}
                              style={{ fontSize: "12px" }}
                              onChange={(e) =>
                                handleFieldChange(
                                  item.year,
                                  month,
                                  e.target.value
                                )
                              }
                              disabled={!isEditable}
                            />
                          </Tooltip>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ROIDetailsDrawer;
